import { UserTypes } from 'modules/User';

import { DefaultMapConfig, MapDataIndex, MapMenuButtonConfig } from './MapWidgetMenu.types';

/**
 * Filters the map menu button configurations based on the search text.
 * @param configs - An array of map menu button configurations.
 * @param lowerCaseSearchText - The search text in lower case to filter the configurations.
 * @returns The filtered map menu button configurations.
 */
export function filterMapMenuButtonConfigs(
  configs: MapMenuButtonConfig[],
  lowerCaseSearchText: string,
): MapMenuButtonConfig[] {
  const filteredConfigs: MapMenuButtonConfig[] = [];

  if (!lowerCaseSearchText) return configs.filter((config) => config.shouldShowAsDefaults);

  // Possible optimization: using .reduce
  for (const config of configs) {
    const { label } = config;

    if (label.toLowerCase().includes(lowerCaseSearchText)) {
      filteredConfigs.push(config);
    }
  }

  return filteredConfigs;
}

/**
 * Filters an array of regions based on the search key.
 * Returns a maximum of 4 matching regions.
 *
 * @param {string[]} regions - The array of regions to filter.
 * @param {string} searchKey - The search key to match against regions.
 * @returns {string[]} - An array of matching regions, up to a maximum of 4.
 */
export function getFilteredRegionSuggestions(regions: string[], searchKey: string): string[] {
  const trimmedSearchKey = searchKey.trim();
  if (trimmedSearchKey === '') {
    return [];
  }

  const searchTerms = trimmedSearchKey.split(' ');
  const topResults = regions
    .filter((region) => {
      const regionTerms = region.split(' ');
      return searchTerms.every((term) =>
        regionTerms.some((regionTerm) => regionTerm.toLowerCase().startsWith(term.toLowerCase())),
      );
    })
    .slice(0, 4);

  return topResults;
}

/**
 * Generates an array of map menu button configurations based on the provided mapDataIndex.
 *
 * @param {MapDataIndex} mapDataIndex - The map data index containing map categories and URLs.
 * @param {DefaultMapConfig[]} defaultMaps - An array of default maps for special handling.
 * @param {ReactElement} defaultIcon - The default icon element for map buttons.
 * @param {boolean} [isDefaultMap] - Specifies if the return results need default maps.
 * @returns {MapMenuButtonConfig[]} An array of map menu button configurations.
 */
export function generateMapConfigs(
  mapDataIndex: MapDataIndex,
  defaultMaps: DefaultMapConfig[],
  defaultIcon: string,
  isDefaultMap: boolean,
): MapMenuButtonConfig[] {
  const buttonConfigs: MapMenuButtonConfig[] = [];

  for (const mapCategory in mapDataIndex) {
    if (mapDataIndex.hasOwnProperty(mapCategory) && mapCategory !== 'version') {
      const mapCategoryObject = mapDataIndex[mapCategory as keyof MapDataIndex] as {
        [key: string]: string;
      };

      for (const mapName in mapCategoryObject) {
        const importMapUrl = mapCategoryObject[mapName];
        const constructedConfig = constructConfig(mapName, importMapUrl, defaultIcon, defaultMaps, isDefaultMap);
        if (constructedConfig) buttonConfigs.push(constructedConfig);
      }
    }
  }

  return buttonConfigs;
}

/**
 * Constructs a map menu button configuration object based on the provided map name and import URL.
 *
 * @param {string} mapName - The name of the map.
 * @param {string} importMapUrl - The URL to import the map data.
 * @param {ReactElement} defaultIcon - The default icon for the button.
 * @param {DefaultMapConfig[]} defaultMaps - An array of default maps.
 * @param {boolean} skipNonDefaultMaps - Indicates if generating config skips non default maps.
 * @returns {MapMenuButtonConfig | undefined} A map menu button configuration object, if available.
 */
function constructConfig(
  mapName: string,
  importMapUrl: string,
  defaultIcon: string,
  defaultMaps: DefaultMapConfig[],
  skipNonDefaultMaps: boolean,
): MapMenuButtonConfig | undefined {
  const defaultMap = defaultMaps.find((item) => item.originalName === mapName);

  // If we only need the 8 default maps,
  if (skipNonDefaultMaps) {
    // It must be found from the defaultMaps array to be generated.
    if (defaultMap) {
      return {
        label: defaultMap?.newName || mapName,
        icon: defaultMap?.newIcon || defaultIcon,
        permission: defaultMap?.newPermission || UserTypes.Premium,
        shouldShowAsDefaults: true,
        importMapUrl,
      };
    }
  } else {
    // If no maps are skipped
    if (defaultMap) {
      // We still make sure default maps work properly
      return {
        label: defaultMap?.newName || mapName,
        icon: defaultMap?.newIcon || defaultIcon,
        permission: defaultMap?.newPermission || UserTypes.Premium,
        shouldShowAsDefaults: false,
        importMapUrl,
      };
    } else {
      // Then we generate other generic maps.
      return {
        label: mapName,
        icon: defaultIcon,
        permission: UserTypes.Premium,
        shouldShowAsDefaults: false,
        importMapUrl,
      };
    }
  }
}

/**
 * Get the button configurations based on the chosen search term.
 *
 * @param {string | undefined} chosenTerm - The chosen search term. If undefined, all default button configurations are returned.
 * @param {MapMenuButtonConfig[]} defaultConfigs - An array of default button configurations.
 * @param {MapMenuButtonConfig[]} allConfigs - An array of all available button configurations.
 * @returns {MapMenuButtonConfig[]} An array of button configurations based on the search term or all default button configurations if no search term is provided.
 */
export function getButtonConfigs(
  chosenTerm: undefined | string,
  defaultConfigs: MapMenuButtonConfig[],
  allConfigs: MapMenuButtonConfig[],
) {
  if (!chosenTerm) return defaultConfigs;
  return filterMapMenuButtonConfigs(allConfigs, chosenTerm.toLowerCase());
}

export { constructConfig };
